import { computed, ref } from 'vue';
import { useChatStore } from '@/stores/app/chatStore';
import { useAuthStoreUser } from '@/stores/app/authUser';
import { useQuadroKanbanStore } from '@/stores/app/quadroKanbanStore';
import { ResponseQuadroKanban } from '@/services/kanban/quadro-kanban.service';
import { Chat } from '@/mocks/app/mockChatData';
import { toast } from 'vue3-toastify';

/**
 * Lógica compartilhada da feature "Mover para Quadro" (kebab do ChatHeader e do
 * ChatItem). Regra única de acesso: só quadros da conta atual aos quais o usuário
 * tem acesso; mantém os atendentes atuais do chat (move o próprio atendimento,
 * sem reatribuir responsável).
 *
 * @param getChat getter reativo do chat-alvo (activeChat no header, props.chat no card).
 */
export function useMoverQuadro(getChat: () => Chat | null | undefined) {
    const chatStore = useChatStore();
    const authUserStore = useAuthStoreUser();
    const quadroStore = useQuadroKanbanStore();

    const showModal = ref(false);
    const moving = ref(false);

    // Quadros-alvo do modal: GLOBAIS da conta + os PESSOAIS do próprio usuário.
    // (idConta/idUsuario normalizados p/ string — convenção de comparação de ids do projeto.)
    const quadrosDisponiveis = computed<ResponseQuadroKanban[]>(() => {
        const currentContaId = String(authUserStore.currentConta?.id ?? '');
        const uid = String(authUserStore.user?.id ?? '');

        return quadroStore.quadros.filter(q => {
            if (q.del) return false;
            if (String(q.idConta) !== currentContaId) return false;
            if (q.visibilidade === 'GLOBAL') return true;
            return q.idUsuario != null && String(q.idUsuario) === uid;
        });
    });

    // Só é "meu atendimento" se o usuário logado está entre os atendentes.
    const isMeuAtendimento = computed<boolean>(() => {
        const chat = getChat();
        const uid = authUserStore.user?.id;
        return !!chat && !!uid && Array.isArray(chat.atendentes) && chat.atendentes.some(a => a.id === uid);
    });

    // Gate do item "Mover para Quadro": só o atendente do atendimento vê a opção.
    // Sem quadro-alvo disponível, o modal trata o estado vazio — não escondemos o item.
    const podeMover = computed<boolean>(() => isMeuAtendimento.value);

    const currentQuadroId = computed<string | null>(() => getChat()?.idQuadro ?? null);

    const currentQuadroNome = computed<string>(() => {
        const id = currentQuadroId.value;
        if (!id) return 'Fila / Sem quadro';
        return quadroStore.quadros.find(q => q.id === id)?.nome ?? 'Fila / Sem quadro';
    });

    // Garante que a lista de quadros esteja carregada (idempotente).
    function ensureQuadrosLoaded() {
        const idUsuario = authUserStore.user?.id;
        if (idUsuario && !quadroStore.initialized) quadroStore.init(idUsuario).catch(() => {});
    }

    function abrirModal() {
        ensureQuadrosLoaded();
        showModal.value = true;
    }

    // Recebe o payload do MoverQuadroModal ({ quadro, enviarMensagem }).
    async function moverParaQuadro({ quadro, enviarMensagem }: { quadro: ResponseQuadroKanban; enviarMensagem: boolean }) {
        const chat = getChat();
        if (!chat || !quadro || chat.idQuadro === quadro.id) {
            showModal.value = false;
            return;
        }

        moving.value = true;
        try {
            await chatStore.updateQuadroEAtendentesChat(chat.id, quadro.id, chat.atendentes ?? null, enviarMensagem);
            toast.success(`Atendimento movido para "${quadro.nome}".`);
            showModal.value = false;
        } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Não foi possível mover o atendimento de quadro.');
        } finally {
            moving.value = false;
        }
    }

    return {
        showModal,
        moving,
        quadrosDisponiveis,
        isMeuAtendimento,
        podeMover,
        currentQuadroId,
        currentQuadroNome,
        ensureQuadrosLoaded,
        abrirModal,
        moverParaQuadro,
    };
}
